import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";

const BranchContext = createContext(null);

function storageKey(clinicId) {
  return clinicId ? `dental_os_active_branch:${clinicId}` : null;
}

function readStoredBranchId(clinicId) {
  const key = storageKey(clinicId);
  if (!key || typeof window === "undefined") return "";
  return window.localStorage.getItem(key) || "";
}

function writeStoredBranchId(clinicId, branchId) {
  const key = storageKey(clinicId);
  if (!key || typeof window === "undefined") return;
  if (branchId) window.localStorage.setItem(key, branchId);
  else window.localStorage.removeItem(key);
}

export function BranchProvider({ children }) {
  const { activeClinicId, activeClinic } = useAuth();
  const clinicId = activeClinicId || activeClinic?.clinic_id || activeClinic?.id || "";
  const [branches, setBranches] = useState([]);
  const [activeBranchId, setActiveBranchId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reloadBranches = useCallback(async () => {
    if (!clinicId) {
      setBranches([]);
      setActiveBranchId("");
      return [];
    }

    setLoading(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc("list_accessible_clinic_branches");
    setLoading(false);

    if (rpcError) {
      setError(rpcError);
      setBranches([]);
      setActiveBranchId("");
      return [];
    }

    const rows = Array.isArray(data) ? data.filter((branch) => branch.status === "active") : [];
    setBranches(rows);
    setActiveBranchId((current) => {
      const stored = readStoredBranchId(clinicId);
      const preferred = current || stored;
      const next = rows.some((branch) => branch.id === preferred) ? preferred : (rows[0]?.id || "");
      writeStoredBranchId(clinicId, next);
      return next;
    });
    return rows;
  }, [clinicId]);

  useEffect(() => {
    reloadBranches();
  }, [reloadBranches]);

  const switchBranch = useCallback((branchId) => {
    if (!branches.some((branch) => branch.id === branchId)) return;
    setActiveBranchId(branchId);
    writeStoredBranchId(clinicId, branchId);
  }, [branches, clinicId]);

  const activeBranch = useMemo(
    () => branches.find((branch) => branch.id === activeBranchId) || null,
    [branches, activeBranchId],
  );

  const value = useMemo(() => ({
    branches,
    activeBranch,
    activeBranchId,
    loading,
    error,
    reloadBranches,
    switchBranch,
  }), [branches, activeBranch, activeBranchId, loading, error, reloadBranches, switchBranch]);

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
}

export function useBranch() {
  const ctx = useContext(BranchContext);
  if (!ctx) {
    return {
      branches: [],
      activeBranch: null,
      activeBranchId: "",
      loading: false,
      error: null,
      reloadBranches: async () => [],
      switchBranch: () => {},
    };
  }
  return ctx;
}
